import type { ResolvedElement } from '../../../types/preview';
import type {
  CanvasElement,
  TextElement,
  ShapeElement,
  ImageElement,
  ButtonElement,
} from '../../../types/index';

interface BackgroundRendererProps {
  resolvedElements: ResolvedElement[];
  canvasWidth: number;
  canvasHeight: number;
  zoom: number;
  showBoundaries: boolean;
  showLayerIndicators: boolean;
}

export default function BackgroundRenderer({
  resolvedElements,
  canvasWidth,
  canvasHeight,
  zoom,
  showBoundaries,
  showLayerIndicators,
}: BackgroundRendererProps) {
  const bgElements = resolvedElements
    .filter((re) => re.resolved.layer === 'BACKGROUND_LAYER' && re.resolved.visible)
    .sort((a, b) => a.resolved.zIndex - b.resolved.zIndex);

  return (
    <div
      className="relative overflow-hidden bg-white"
      style={{
        width: canvasWidth * zoom,
        height: canvasHeight * zoom,
      }}
    >
      {bgElements.map((re) => (
        <BgElementRenderer
          key={re.resolved.id}
          element={re.resolved}
          zoom={zoom}
          showBoundary={showBoundaries}
          showLayerIndicator={showLayerIndicators}
          fullyResolved={re.fullyResolved}
        />
      ))}

      {bgElements.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <p className="text-sm text-gray-400">No background elements</p>
            <p className="text-xs text-gray-500 mt-1">
              Assign elements to BACKGROUND_LAYER to see them here
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

interface BgElementRendererProps {
  element: CanvasElement;
  zoom: number;
  showBoundary: boolean;
  showLayerIndicator: boolean;
  fullyResolved: boolean;
}

function BgElementRenderer({
  element,
  zoom,
  showBoundary,
  showLayerIndicator,
  fullyResolved,
}: BgElementRendererProps) {
  const baseStyle: React.CSSProperties = {
    position: 'absolute',
    left: element.x * zoom,
    top: element.y * zoom,
    width: element.width * zoom,
    height: element.height * zoom,
    opacity: element.opacity,
    transform: element.rotation ? `rotate(${element.rotation}deg)` : undefined,
    transformOrigin: 'center center',
    outline: showBoundary ? '1px dashed rgba(255, 255, 255, 0.3)' : undefined,
    boxShadow: showLayerIndicator
      ? 'inset 0 0 0 2px rgba(251, 191, 36, 0.5)'
      : undefined,
  };

  const unresolvedOutline: React.CSSProperties = !fullyResolved
    ? { outline: '2px dashed rgba(239, 68, 68, 0.5)' }
    : {};

  switch (element.type) {
    case 'text':
      return <BgText element={element} style={{ ...baseStyle, ...unresolvedOutline }} zoom={zoom} />;
    case 'shape':
      return <BgShape element={element} style={{ ...baseStyle, ...unresolvedOutline }} zoom={zoom} />;
    case 'image':
      return <BgImage element={element} style={{ ...baseStyle, ...unresolvedOutline }} zoom={zoom} />;
    case 'button':
      return <BgButton element={element} style={{ ...baseStyle, ...unresolvedOutline }} zoom={zoom} />;
    default:
      return null;
  }
}

function BgText({
  element,
  style,
  zoom,
}: {
  element: TextElement;
  style: React.CSSProperties;
  zoom: number;
}) {
  return (
    <div
      style={{
        ...style,
        display: 'flex',
        alignItems: 'center',
        justifyContent:
          element.textAlign === 'center'
            ? 'center'
            : element.textAlign === 'right'
            ? 'flex-end'
            : 'flex-start',
        fontFamily: element.fontFamily || 'Inter, sans-serif',
        fontSize: (element.fontSize || 16) * zoom,
        fontWeight: element.fontWeight || 400,
        fontStyle: element.fontStyle || 'normal',
        textDecoration: element.textDecoration || 'none',
        textTransform: (element.textTransform || 'none') as React.CSSProperties['textTransform'],
        lineHeight: element.lineHeight || 1.4,
        letterSpacing: element.letterSpacing ? `${element.letterSpacing * zoom}px` : undefined,
        color: element.color || '#000000',
        overflow: 'hidden',
        userSelect: 'none',
        pointerEvents: 'none',
      }}
      role="presentation"
      aria-hidden="true"
    >
      <span>{element.text || 'Text'}</span>
    </div>
  );
}

function BgShape({
  element,
  style,
  zoom,
}: {
  element: ShapeElement;
  style: React.CSSProperties;
  zoom: number;
}) {
  if (element.shapeType === 'line') {
    return (
      <div
        style={{
          ...style,
          height: Math.max((element.strokeWidth || 1) * zoom, 1),
          backgroundColor: element.stroke || element.fill || '#000000',
        }}
      />
    );
  }

  const shapeStyle: React.CSSProperties = {
    ...style,
    backgroundColor: element.fill || 'transparent',
    opacity: (element.opacity ?? 1) * (element.fillOpacity ?? 1),
    border: element.stroke && element.stroke !== 'transparent'
      ? `${(element.strokeWidth || 1) * zoom}px solid ${element.stroke}`
      : undefined,
  };

  if (element.shapeType === 'circle') {
    shapeStyle.borderRadius = '50%';
  } else if (element.shapeType === 'rounded-rect') {
    shapeStyle.borderRadius = `${(element.borderRadius || 8) * zoom}px`;
  }

  return <div style={shapeStyle} />;
}

function BgImage({
  element,
  style,
  zoom,
}: {
  element: ImageElement;
  style: React.CSSProperties;
  zoom: number;
}) {
  if (!element.src) {
    return (
      <div
        style={{
          ...style,
          backgroundColor: '#1a1a2e',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <span style={{ fontSize: 11 * zoom, color: 'rgba(255,255,255,0.3)' }}>
          No image
        </span>
      </div>
    );
  }

  return (
    <div style={{ ...style, overflow: 'hidden' }}>
      <img
        src={element.src}
        alt=""
        style={{
          width: '100%',
          height: '100%',
          objectFit: element.objectFit || 'cover',
        }}
        aria-hidden="true"
      />
    </div>
  );
}

function BgButton({
  element,
  style,
  zoom,
}: {
  element: ButtonElement;
  style: React.CSSProperties;
  zoom: number;
}) {
  return (
    <div
      style={{
        ...style,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: element.backgroundColor || '#a855f7',
        color: element.color || '#ffffff',
        borderRadius: `${(element.borderRadius || 4) * zoom}px`,
        fontSize: (element.fontSize || 14) * zoom,
        fontWeight: element.fontWeight || 600,
        fontFamily: element.fontFamily || 'Inter, sans-serif',
        paddingLeft: `${(element.paddingX || 16) * zoom}px`,
        paddingRight: `${(element.paddingX || 16) * zoom}px`,
        paddingTop: `${(element.paddingY || 8) * zoom}px`,
        paddingBottom: `${(element.paddingY || 8) * zoom}px`,
        cursor: 'default',
        userSelect: 'none',
        pointerEvents: 'none',
      }}
      role="presentation"
      aria-hidden="true"
    >
      {element.text || 'Button'}
    </div>
  );
}
